import * as Highlight from 'react-highlighter';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { LocalizedText } from 'bernie-l10n';

import {
  MoreReviewsButtonProps,
  ReviewByLineModel,
  ReviewCardOnDialogModel,
  ReviewCardOnInfositeModel,
  ReviewManagerDetailsModel,
  ReviewManagerResponseModel,
  ReviewPropsModel,
  ReviewRemarkModel,
  ReviewStayDurationModel,
  ReviewSubmissionTimeModel,
  ReviewTitleModel,
  ReviewTravelersModel,
  SeeAllReviewsButtonProps
} from 'models/common';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkSecondaryButton } from 'uitk-react-button';

import { LocalizedToggle } from 'components/common/localized-toggle';
import { MAX_RATING } from 'components/infosite/property-details/bar-ratings';
import { Review } from 'components/infosite/property-details/review';

export const ReviewRating = ({ ratingOverall, superlative }: ReviewTitleModel) => (
  <h3 className="uitk-type-heading-500 all-b-padding-three">
    {ratingOverall}/{MAX_RATING} {superlative}
  </h3>
);
ReviewRating['displayName'] = 'ReviewTitle';

export const ReviewSubmissionTime = ({ reviewSubmissionTime, l10n }: ReviewSubmissionTimeModel) => (
  <span className="review-meta-date uitk-type-300 pwa-theme--grey-600">
    {l10n.formatDateString(reviewSubmissionTime, { skeleton: 'yMMMd' })}
  </span>
);
ReviewSubmissionTime['displayName'] = 'ReviewSubmissionTime';

export const ReviewStayDuration = ({ stayDuration }: ReviewStayDurationModel) => (
  <div className="review-stay-duration uitk-type-200 pwa-theme--grey-600 all-t-margin-two">{stayDuration}</div>
);
ReviewStayDuration['displayName'] = 'ReviewStayDuration';

export const ReviewText = ({ l10n, searchText, reviewTranslationState, translatedText, review }: ReviewPropsModel) => (
  <div className="review-text uitk-type-300 pwa-theme--grey-700">
    {review.reviewText || (review.themes && review.themes.length) ? (
      <p className="review-text-body">
        {reviewTranslationState !== 'original' && translatedText && <span className="translated-review-text">{translatedText}</span>}
        {reviewTranslationState !== 'translated' && searchText && <Highlight search={searchText}>{review.reviewText}</Highlight>}
        {reviewTranslationState !== 'translated' && !searchText && <span>{review.reviewText}</span>}
      </p>
    ) : (
      <span data-stid="ratings-only-message">
        <LocalizedText message="hotels.infosite.reviews.reviewsNoCommentMessage.text" />
      </span>
    )}
    {review.positiveRemarks && (
      <ReviewRemark
        icon="add"
        title={l10n.formatText('hotels.infosite.reviews.pros.title')}
        remark={review.positiveRemarks}
        searchText={searchText}
      />
    )}
    {review.negativeRemarks && (
      <ReviewRemark
        icon="remove"
        title={l10n.formatText('hotels.infosite.reviews.cons.title')}
        remark={review.negativeRemarks}
        searchText={searchText}
      />
    )}
    {review.locationRemarks && (
      <ReviewRemark
        icon="place"
        title={l10n.formatText('hotels.infosite.reviews.location.title')}
        remark={review.locationRemarks}
        searchText={searchText}
      />
    )}
  </div>
);
ReviewText['displayName'] = 'ReviewText';

export const ReviewByline = ({ l10n, author, userLocation }: ReviewByLineModel) => (
  <div className="pwa-theme--grey-700 uitk-type-heading-300">
    {author && userLocation && <span>{`${author}, ${userLocation}`}</span>}
    {author && !userLocation && <span>{author}</span>}
    {!author && userLocation && <span>{userLocation}</span>}
  </div>
);
ReviewByline['displayName'] = 'ReviewByline';

export const ReviewRemark = ({ icon, title, remark, searchText }: ReviewRemarkModel) => (
  <p className="review-callout uitk-type-200 pwa-theme--grey-600">
    <UitkIcon name={icon} size={UitkIconSize.SMALL} />
    <span className="callout-text">
      <strong className="uitk-type-200__bold">{`${title}: `}</strong>
      {searchText && (
        <span>
          <Highlight search={searchText}>{remark}</Highlight>
        </span>
      )}
      {!searchText && <span>{remark}</span>}
    </span>
  </p>
);
ReviewRemark['displayName'] = 'ReviewRemark';

export const ReviewManagerResponse = ({ managerResponse }: ReviewManagerResponseModel) => (
  <LocalizedToggle className="reviews-toggle">
    <span className="uitk-type-200 pwa-theme--grey-700">{managerResponse.response}</span>
  </LocalizedToggle>
);
ReviewManagerResponse['displayName'] = 'ReviewManagerResponse';

export const ReviewCardOnInfosite = ({ hideTopPadding, review, showBottomSeparator }: ReviewCardOnInfositeModel) => {
  const reviewCardClassNames = [];
  if (showBottomSeparator) {
    reviewCardClassNames.push('uitk-card-separator-bottom');
  }

  return (
    <UitkCard key={review.reviewId}>
      <UitkCardContent className={reviewCardClassNames.join(' ')}>
        <Review review={review} hideManagerResponses={true} hideTopPadding={hideTopPadding} />
      </UitkCardContent>
    </UitkCard>
  );
};
ReviewCardOnInfosite['displayName'] = 'ReviewCardOnInfosite';

export const ReviewCardOnDialog = ({ index, review, areTabsPresent, hideTopPadding }: ReviewCardOnDialogModel) => {
  const firstReview = index === 0;

  return (
    <div key={review.reviewId} className="uitk-card-separator-bottom">
      {firstReview ? <Review review={review} hideTopPadding={hideTopPadding} /> : <Review review={review} />}
    </div>
  );
};
ReviewCardOnDialog['displayName'] = 'ReviewCardOnDialog';

export const ReviewManagerDetails = ({ l10n, managerResponse }: ReviewManagerDetailsModel) => (
  <span className="uitk-type-bold pwa-theme--grey-700">
    {managerResponse.userNickname && (
      <div className="all-y-padding-one">
        {l10n.formatText(
          'hotels.infosite.reviews.userResponseFrom.text',
          managerResponse.userNickname,
          managerResponse.date ? l10n.formatDateString(managerResponse.date, { skeleton: 'yMMMd' }) : ''
        )}
      </div>
    )}
  </span>
);
ReviewManagerDetails['displayName'] = 'ReviewManagerDetails';

export const MoreReviewsButton = ({ onMoreReviews, rfrrId }: MoreReviewsButtonProps) => {
  const ButtonTracked = ClickTracker(UitkSecondaryButton);

  return (
    <section className="more-reviews">
      <ButtonTracked
        className="more-reviews-button"
        onClick={onMoreReviews}
        linkName="More Reviews"
        rfrr={rfrrId}
        events="event419=10"
        text={<LocalizedText message="hotels.infosite.paginationButton.label" />}
      />
    </section>
  );
};
MoreReviewsButton['displayName'] = 'MoreReviewsButton';

export const SeeAllReviewsButton = ({ onClick }: SeeAllReviewsButtonProps) => {
  const [initialized, setInitialized] = React.useState(false);
  const ButtonTracked = ClickTracker(UitkSecondaryButton);

  React.useEffect(() => {
    setInitialized(true);
  }, []);

  return (
    <ButtonTracked
      onClick={onClick}
      linkName="See All Reviews"
      rfrr="Reviews.ClickSeeAllReviewsToOpenDialog"
      events="event419=10"
      text={<LocalizedText message="hotels.infosite.reviews.seeAllReviews" />}
      data-stid="see-all-reviews"
      disabled={!initialized}
    />
  );
};
SeeAllReviewsButton['displayName'] = 'SeeAllReviewsButton';

export interface RatingWithSuperlativeModel {
  rating?: string;
  superlative?: string;
}

export const RatingWithSuperlative = ({ rating, superlative }: RatingWithSuperlativeModel) => (
  <div className="reviews-overall-rating-superlative uitk-type-bold uitk-type-heading-600">
    {rating}/{MAX_RATING} {superlative}
  </div>
);

export const ReviewTraveler = ({ travelers }: ReviewTravelersModel) => (
  <div className="uitk-type-300 pwa-theme--grey-700">{travelers.join(', ')}</div>
);
ReviewTraveler['displayName'] = 'ReviewTraveler';
